import React from 'react';
import ReactDOM from 'react-dom';
import styled from 'astroturf/react';

import { ModalElementContext } from 'js/design_system/Modal';
import _Backdrop from "Modal-Backdrop.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/components/modals/Modal.tsx?styleId=Backdrop";
import _Container from "Modal-Container.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/components/modals/Modal.tsx?styleId=Container";
import _CloseButton from "Modal-CloseButton.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/components/modals/Modal.tsx?styleId=CloseButton";
import _BackButton from "Modal-BackButton.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/components/modals/Modal.tsx?styleId=BackButton";


// These should eventually be updated to come from the design system
const DESKTOP_SIZE = 1140;
const TABLET_SIZE = 780;
const PHONE_SIZE = 430;

export type Props = {
  // The id of the title element in the modal.
  // This is optional but strongly recommended for improved accessibility
  ariaLabelledby?: string;
  // When true, the modal will fade and slide in,
  // and the backdrop will fade in
  animateIn?: boolean;
  /** For custom styling of the backdrop. */
  backdropClassName?: string;
  children?: React.ReactNode;
  /**
   * For custom styling of the modal container.
   * This prop can be passed manually, or using astroturf via `styled(Modal)`.
   */
  className?: string;
  /** Called when clicking the close button or the backdrop. */
  onClose: () => void;
  /** When true, show a close button in the top right corner of the modal. */
  showCloseButton?: boolean;
  /** Called when clicking the back button. */
  onBack?: () => void;
  /** When true, show a back button in the top left corner of the modal.  */
  showBackButton?: boolean;
  /** For passing HTML properties directly to the modal container */
  containerProps?: React.DetailsHTMLAttributes<HTMLDivElement>;
  /** When the open animation completes */
  onAnimationComplete?: () => void;
};

type State = {
  windowHeight: number;
};

const BACKDROP_IN_KEYFRAMES = [{ opacity: 0 }, { opacity: 1 }];

// For slide in animation
const CONTAINER_IN_KEYFRAMES = [
  { opacity: 0, transform: 'translateY(40px)' },
  { opacity: 1, transform: 'translateY(0)' },
];

const Backdrop = /*#__PURE__*/styled("div", null, {
  displayName: "Backdrop",
  styles: _Backdrop
});

const Container = /*#__PURE__*/styled("div", null, {
  displayName: "Container",
  styles: _Container
});

const CloseButton = /*#__PURE__*/styled("button", null, {
  displayName: "CloseButton",
  styles: _CloseButton
});

const BackButton = /*#__PURE__*/styled("button", null, {
  displayName: "BackButton",
  styles: _BackButton
});

export class Modal extends React.Component<Props, State> {
  backdropRef: HTMLDivElement | null = null;
  containerRef: HTMLDivElement | null = null;
  focusedBefore: HTMLElement | null = null;
  overflowStyle = '';

  state = {
    windowHeight: window.innerHeight,
  };

  static defaultProps = {
    animateIn: true,
    showCloseButton: false,
    showBackButton: false,
    onBack: () => {},
  };

  componentDidMount() {
    const { animateIn, onAnimationComplete } = this.props;
    // Remember existing overflow property
    const { overflow } = window.getComputedStyle(document.body);
    this.overflowStyle = overflow;
    // Set overflow to hidden to disallow scrolling while modal is open
    document.body.style.overflow = 'hidden';

    if (!animateIn) return;
    this.containerRef
      ?.animate(CONTAINER_IN_KEYFRAMES, {
        duration: 320,
        easing: 'ease-out',
      })
      .finished.then(onAnimationComplete);
    this.backdropRef?.animate(BACKDROP_IN_KEYFRAMES, {
      duration: 320,
      easing: 'ease-out',
    });
    // Often activating this will cause focus to jump to an element deep in the html, but then the
    // user won't be able  to navigate to any of the in modal content without going back
    this.focusedBefore = document.activeElement as HTMLElement | null;
    this.focusedBefore?.blur();

    // Watch for resizing so that the backdrop height is always correct
    window.addEventListener('resize', this.watchWindowSize);
  }

  componentWillUnmount() {
    // Remove overflow: hidden from body when modal is closed
    document.body.style.overflow = this.overflowStyle;

    this.focusedBefore?.focus();

    window.removeEventListener('resize', this.watchWindowSize);
  }

  watchWindowSize = () => {
    this.setState({ windowHeight: window.innerHeight });
  };

  render() {
    const {
      ariaLabelledby,
      backdropClassName,
      children = null,
      className,
      onClose,
      showCloseButton,
      onBack,
      showBackButton,
      containerProps = {},
    } = this.props;
    const { windowHeight } = this.state;

    return (
      <ModalElementContext.Consumer>
        {(modalElement) =>
          ReactDOM.createPortal(
            <Backdrop
              style={{ height: windowHeight }}
              className={backdropClassName}
              onClick={(e) => {
                if (e.target !== e.currentTarget) return;
                setTimeout(onClose, 0);
              }}
              ref={(el: HTMLDivElement) => (this.backdropRef = el)}
            >
              <Container
                {...containerProps}
                aria-labelledby={ariaLabelledby}
                className={className}
                ref={(el: HTMLDivElement) => (this.containerRef = el)}
                role="modal"
              >
                {showBackButton && (
                  <BackButton aria-label="Back" onClick={onBack}>
                    {'<'} Back
                  </BackButton>
                )}
                {showCloseButton && (
                  <CloseButton
                    aria-label="Close"
                    onClick={() => setTimeout(onClose, 0)}
                  >
                    ×
                  </CloseButton>
                )}
                {children}
              </Container>
            </Backdrop>,
            document.getElementById('react-modal') ||
              modalElement?.current ||
              document.body,
          )
        }
      </ModalElementContext.Consumer>
    );
  }
}

export default Modal;
